import { Component, computed, input, signal } from '@angular/core';
import { v3ComponentContext } from '../services/v3-component-context';
import { splitInHalf } from '../array-utils';
import { V3ScoreCategoryTab } from '../v3-score-category-tab/v3-score-category-tab';
import { V3CategoryCard } from '../v3-category-card/v3-category-card';
import { toV3ColorVar } from '../v3-scores-overview/score-color';
import { V3TabPanel } from '../v3-tab-panel/v3-tab-panel';
import {
  computeCategoryCards,
  computeScoreTilesWithKey,
  FundamentalScoreSystemKey,
  resolveFundamentalScoresContent,
} from '../services/api/fundamental-scores';

// V3's Fundamental Scores (TraderFox) sub-page — V3's counterpart to V2's
// FundamentalScoresAnalysisV2. Built so far: the top category menu (one tile per system,
// click to select) and the "Bewertung nach Kategorie" cards for the active tab, with real
// Zero and Finnet styling (see CLAUDE.md). The "Gesamtbewertung" gauge isn't built here yet.
@Component({
  selector: 'app-v3-fundamental-scores-analysis',
  imports: [V3ScoreCategoryTab, V3CategoryCard, V3TabPanel],
  standalone: true,
  templateUrl: './v3-fundamental-scores-analysis.html',
  styleUrl: './v3-fundamental-scores-analysis.scss',
})
export class V3FundamentalScoresAnalysis {
  private readonly ctx = v3ComponentContext();
  readonly responsiveService = this.ctx.responsiveService;

  // Set by V3ScoresOverview when navigation came from a specific tile — the category that
  // tab should open on. Null/not-found falls back to the first tile.
  readonly initialCategory = input<FundamentalScoreSystemKey | null>(null);

  readonly isFinnetDesktop = this.ctx.layout.isFinnetDesktop;
  readonly isZeroDesktop = this.ctx.layout.isZeroDesktop;
  readonly isDesktopBreakdown = this.ctx.layout.isDesktop;

  private readonly isGuestMode = this.ctx.isGuestMode;

  private readonly content = computed(() =>
    resolveFundamentalScoresContent(this.ctx.assetsStore.fundamentalScores(), this.isGuestMode()),
  );

  readonly tiles = computed(() =>
    computeScoreTilesWithKey(this.content() ?? {}).map((tile) => ({
      ...tile,
      v3ColorVar: toV3ColorVar(tile.colorVar),
    })),
  );

  // An explicit click always wins once made. Until then, the active tab is derived from
  // initialCategory (falling back to the first tile when absent or not found among the
  // currently available systems).
  private readonly manualIndex = signal<number | null>(null);

  readonly activeIndex = computed(() => {
    const tiles = this.tiles();
    const manual = this.manualIndex();
    if (manual !== null && manual < tiles.length) return manual;
    const key = this.initialCategory();
    if (key) {
      const index = tiles.findIndex((tile) => tile.key === key);
      if (index !== -1) return index;
    }
    return 0;
  });

  selectTab(index: number) {
    this.manualIndex.set(index);
  }

  private readonly activeSystemKey = computed(() => this.tiles()[this.activeIndex()]?.key);

  readonly categoryCards = computed(() => {
    const key = this.activeSystemKey();
    return key ? computeCategoryCards(this.content() ?? {}, key) : [];
  });

  // Mobile only — Red Flags gets its own titled section, separate from "Bewertung nach
  // Kategorie". Desktop merges everything back into one list (see leftColumnCards below) —
  // the two section titles are redundant once it's a single flowing 2-column layout.
  readonly nonRedFlagCards = computed(() => this.categoryCards().filter((card) => !card.isRedFlag));
  readonly redFlagCard = computed(
    () => this.categoryCards().find((card) => card.isRedFlag) ?? null,
  );

  // Desktop only — split by count (not CSS multi-column height-balancing, rejected for the
  // same reason V3ZeroContentGrid/V3FinnetContentGrid already rejected it: unpredictable
  // placement for variable-height items).
  private readonly columnSplit = computed(() => splitInHalf(this.categoryCards()));
  readonly leftColumnCards = computed(() => this.columnSplit()[0]);
  readonly rightColumnCards = computed(() => this.columnSplit()[1]);
}
